import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import styles from "./AdminPage.module.css";
import { IMAGES } from "../../utils/GetImages";
import LanguageSwitcher from "../LanguageSwitcher";
import { getTranslation } from "../../utils/translation";
import { useNavigate } from "react-router-dom";

export default function AdminPage() {
	const [form, setForm] = useState({ userId: "", password: "" });
	const [error, setError] = useState("");
	const [forceUpdate, setForceUpdate] = useState(0);
	const [isReady, setIsReady] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { i18n } = useTranslation();
	const navigate = useNavigate();

	const BackgroundImg = IMAGES["MainBackground.png"];

	const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

	const onBack = () => navigate(-1);

	useEffect(() => {
		const handleLanguageChange = () => setForceUpdate((prev) => prev + 1);
		i18n.on("languageChanged", handleLanguageChange);
		return () => i18n.off("languageChanged", handleLanguageChange);
	}, [i18n]);

	useEffect(() => {
		if (i18n.isInitialized && i18n.store.data) setIsReady(true);
	}, [i18n.isInitialized, i18n.store.data]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setForm((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setIsLoading(true);

		try {
			const response = await fetch(`${API_BASE_URL}/prod/auth/login`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					userId: form.userId,
					password: form.password,
				}),
			});

			const data = await response.json();

			if (!response.ok || !data.accessToken) {
				throw new Error(data.message || "Login failed.");
			}

			localStorage.setItem("adminAccessToken", data.accessToken);
			navigate("/onedot-home");
		} catch (err) {
			setError(getTranslation(i18n, "adminPage.loginError"));
		} finally {
			setIsLoading(false);
		}
	};

	if (!isReady) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
					fontSize: "18px",
				}}
			>
				Loading...
			</div>
		);
	}

	return (
		<div
			className={styles["contact-container"]}
			style={{ backgroundImage: `url(${BackgroundImg})` }}
			key={`${i18n.language}-${forceUpdate}`}
		>
			<div
				className={`${styles["contact-card"]} ${
					isLoading ? styles["card-loading"] : ""
				}`}
			>
				{isLoading && <div className={styles["loading-overlay"]}></div>}
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: "20px",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "10px",
						}}
					>
						<Button
							variant="outlined"
							startIcon={<ArrowBackIcon />}
							onClick={onBack}
							size="small"
						>
							{getTranslation(i18n, "adminPage.back")}
						</Button>
					</div>
					<h2 className={styles["contact-card-title"]}>
						{getTranslation(i18n, "adminPage.title")}
					</h2>
					<LanguageSwitcher />
				</div>

				<form
					onSubmit={handleSubmit}
					className={styles["animated-form"]}
				>
					<label
						className={`${styles["form-label"]} ${styles["form-field-1"]}`}
					>
						<span>
							{getTranslation(i18n, "adminPage.form.userId")}
						</span>
						<input
							className={`${styles["form-input"]} ${styles["animated-input"]}`}
							type="text"
							name="userId"
							value={form.userId}
							onChange={handleChange}
							disabled={isLoading}
							required
						/>
					</label>

					<label
						className={`${styles["form-label"]} ${styles["form-field-2"]}`}
					>
						<span>
							{getTranslation(i18n, "adminPage.form.password")}
						</span>
						<input
							className={`${styles["form-input"]} ${styles["animated-input"]}`}
							type="password"
							name="password"
							value={form.password}
							onChange={handleChange}
							disabled={isLoading}
							required
						/>
					</label>

					{error && (
						<div
							className={styles["error-message"]}
							style={{
								color: "red",
								marginBottom: "1rem",
								textAlign: "center",
							}}
						>
							{error}
						</div>
					)}

					<button
						type="submit"
						className={`${styles["form-button"]} ${
							styles["animated-submit-button"]
						} ${styles["form-field-3"]} ${
							isLoading ? styles["loading"] : ""
						}`}
						disabled={isLoading}
					>
						{isLoading ? (
							<div className={styles["loading-content"]}>
								<div className={styles["spinner"]}></div>
								<span>Logging in...</span>
							</div>
						) : (
							getTranslation(i18n, "adminPage.form.submitButton")
						)}
					</button>
				</form>
			</div>
		</div>
	);
}
